import type { Messages } from '../messages.js'

/**
 * Slovak. `ťah` for flips, the word a Slovak board game uses for a move.
 *
 * Same four plural forms as Czech and the same trap: `many` is for fractions, not for large
 * numbers, so `1,5 slova` takes it and `5 slov` does not.
 */
export const sk: Messages = {
  tag: 'sk',

  readingDictionary: 'Načítavam slovník…',
  noWordList: 'Pre „{language}“ chýba zoznam slov. Vytvorte ho:  pnpm dictionary build',
  emptyWordList: 'Zoznam slov pre „{language}“ je prázdny.',

  flips: 'ťahy',
  score: 'skóre',
  words: 'slová',
  round: 'kolo',
  ticksLeftLabel: 'Zostávajúci čas v tomto kole',
  typeAWord: 'napíšte slovo',
  tapPrompt: 'ťuknutím vyberte alebo vráťte písmená, potom {action}',

  boardOfTiles: 'Hracia plocha z {n} kameňov',
  faceDown: 'lícom nadol',
  wildCard: 'žolík',
  wildKey: 'ľubovoľné písmeno',
  letterReplaced: '{from} sa zmenilo na {to}',
  letterSwap: 'VÝMENA PÍSMEN!',
  spentTile: 'použitý kameň',
  hiddenWhilePaused: 'skryté počas prestávky',
  letterInWord: '{letter}, {position}. písmeno slova',

  completeWord: 'Dokončiť slovo',

  completeShort: 'Dokončiť',
  reset: 'Vymazať',
  pause: 'Prestávka',
  resume: 'Pokračovať',
  newGame: 'Nová hra',
  paused: 'Pozastavené',
  outOfFlips: 'Minuli sa ťahy',
  finalResult: '{score} za {words} v {rounds}',
  playAgain: 'Hrať znova',
  share: 'Zdieľať',
  shareCopied: 'Skopírované.',
  shareSelect: 'Skopírujte toto:',

  lettersSelect: 'písmená vyberajú',
  keysWild: 'vezme sa, keď napíšete písmeno, ktoré na ploche nie je',
  clearsEvery: 'vymaže všetky vybrané {letter}',
  undoLastLetter: 'vrátiť posledné písmeno',
  noWordsYet: 'Zatiaľ žiadne slová.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'už nájdené',
  reasonTooShort: 'príliš krátke',
  reasonNotAWord: 'nie je to slovo',
  reasonAllFound: 'už ich máte všetky',
  noSuchLetterUp: 'žiadne odkryté {letter}',
  nothingUp: 'nič nie je odkryté',
  letterHidden: 'jedno písmeno sa skrylo',
  shuffled: 'zamiešané',
  shuffledAndBilled: 'zamiešané, započítaných {flips} nevyužitých',
  tooFewLetters: 'Zostalo príliš málo písmen',

  gameLanguage: 'jazyk',
  interfaceLanguage: 'rozhranie',
  dictionarySize: '{common} bežných z {full} slov',
  filterLanguages: 'Hľadať jazyk',
  noMatches: 'Žiadna zhoda',

  nerdMode: 'režim pre nadšencov',
  rules: 'Pravidlá',
  difficulty: 'obťažnosť',
  difficultyNames: { easy: 'ľahká', medium: 'stredná', hard: 'ťažká', insane: 'šialená' },
  pausedNoBoard: 'Táto hra sa do tabuľky nedostane.',
  about: 'O hre',
  themeLabel: 'téma',
  themeNames: { traditional: 'tradičná', light: 'svetlá', contrast: 'vysoký kontrast' },
  tiles: 'kamene (N)',
  secondsPerTick: 'sekundy / takt',
  holdTicks: 'takty výdrže',
  minWord: 'najkratšie slovo',
  startingFlips: 'ťahy na začiatku',
  wildChance: 'šanca na žolíka',
  replaceChance: 'šanca na výmenu písmen',
  hideChance: 'šanca na skrytie písmena',
  wordCompleteMode: 'po dokončení slova',
  wordCompleteNames: { shuffle: 'zamiešať', spend: 'minúť', keep: 'ponechať' },
  flipEconomy: 'návrat ťahov',
  flipEconomyNames: {
    none: 'žiadny',
    perLetter: 'za písmeno',
    fibonacci: 'fibonacci',
    overMinimum: 'nad minimum',
  },
  repeatedLetterKey: 'kláves opakovaného písmena',
  keySchemeNames: { cycle: 'cyklus', advance: 'postup' },
  keySchemeHelp: {
    cycle:
      'A vezme ďalšie nepoužité A, a keď sú všetky v slove, vymaže ich. ' +
      'Shift+A ich vymaže tiež.',
    advance: 'A vezme ďalšie nepoužité A. Shift+A vymaže zo slova všetky A.',
  },

  whatThatMeans: 'Čo to znamená',
  factRound: 'kolo',
  factWholeBoardUp: 'celá plocha odkrytá na',
  factRoundCosts: 'kolo stojí',
  factFlipsBuy: 'počiatočné ťahy kúpia',
  factThisBoard: 'táto plocha',
  factBoardHadToAdmit: 'plocha musela pripustiť',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, najdlhšie {longest}',
  wordsIncludingOneOf: '{words}, z toho jedno z {ceiling}',
  scorelessRounds: '{rounds} bez bodov',

  whatAWordPays: 'Čo slovo vynesie',
  columnLetters: 'písmená',
  columnCost: 'cena',
  columnPoints: 'body',
  columnFlips: 'ťahy',
  columnNet: 'rozdiel',

  canonicalRules: 'Štandardné pravidlá pre {difficulty}.',
  customRules: 'Zmenené oproti prednastaveniu. Skóre podľa vlastných pravidiel sa nezapočítava.',
  applyAndStart: 'Použiť a začať novú hru',
  changesNextGame: 'Zmeny sa prejavia v ďalšej hre.',
  presets: 'Prednastavenia:',

  start: 'Začať',
  restart: 'Znova',
  quit: 'Ukončiť',
  quitTitle: 'Ukončiť túto hru?',
  restartTitle: 'Začať túto hru znova?',
  restartConfirm: 'Znova',
  quitConfirm: 'Ukončiť',
  keepPlaying: 'Hrať ďalej',
  personalBest: 'Vaše najlepšie hry',
  newPersonalBest: 'Nový osobný rekord.',
  columnRank: '#',
  notRanked: 'Vlastné pravidlá, takže táto hra sa nezapočítava.',
  notRankedPaused: 'Hodiny sa zastavili, takže táto hra sa nezapočítava.',
  rankOfTotal: '{rank} z {total}',

  howToPlay: 'Ako hrať',

  backToGame: 'Späť do hry',
  welcomeTitle: 'Vitajte v Blinkered',
  tutorialSkip: 'Preskočiť',
  tutorialNext: 'Ďalej',
  tutorialBack: 'Späť',
  tutorialStart: 'Začať hrať',
  tutorialHideAgain: 'Nabudúce nezobrazovať',
  tutorialProgress: '{n} z {total}',
  tutorialSkipTitle: 'Preskočiť úvod?',
  tutGoalTitle: 'Blinkered je pretek',
  tutGoalBody:
    'Hru začínate s pevným počtom ťahov a každý odkrytý kameň stojí jeden ťah. Keď sa ťahy minú, hra sa končí. Za slová ťahy získavate, a čím dlhšie slovo, tým viac ťahov dostanete.',
  tutSpentBody:
    'Písmená, ktoré použijete v slove, zostanú zakryté až do ďalšieho kola. Ktoré písmená sú na ploche, zmení len výmena písmena.',
  tutChangeTitle: 'Písmená sa môžu meniť',
  tutSameLetters:
    'Všimli ste si slovo, ktoré ste nestihli zahrať? Rovnaké písmená sa vrátia v ďalšom kole, zamiešané na nové miesta.',
  tutPickLetters: 'Ťukajte na písmená v poradí, v akom chcete zložiť slovo.',
  tutMoreTurn: 'Kamene sa odkrývajú, aj kým premýšľate, takže lepšie písmeno môže ešte prísť.',
  tutTapBack:
    'Vzali ste písmeno, ktoré ste nechceli? Ťuknite naň znova a vráti sa. Ktorékoľvek, nielen posledné.',
  tutComplete: 'Keď je slovo hotové, stlačte Dokončiť.',
  htBoardTitle: 'Plocha',
  htBoardBody: 'Kamene sa odkrývajú po jednom, v náhodnom poradí. Z odkrytých sa skladajú slová.',
  htWordsTitle: 'Slová',
  htWordsBody: 'Slovo zložíte z odkrytých kameňov písaním alebo klikaním na písmená v poradí.',
  htFlipsTitle: 'Ťahy',
  htFlipsBody:
    'Každý odkrytý kameň stojí ťah. Dokončené slovo ťahy vráti, a dlhšie slová vrátia viac. Keď sa ťahy minú, hra sa končí.',
  htRoundTitle: 'Kolo',
  htRoundBody:
    'Keď sa odkryje posledný kameň kola, celá plocha na chvíľu vydrží. Potom sa kamene otočia a zamiešajú a začne nové kolo.',
  htLanguagesTitle: 'Jazyky',
  htLanguagesBody:
    'Je ich {n}. Každú plochu možno vyriešiť slovami, ktoré ľudia naozaj používajú. Nezvyčajné slovo boduje tiež, ak ho slovník pozná.',
  htKeysTitle: 'Klávesnica',
  htWildTitle: 'Žolíci',
  htWildBody:
    'Občas sa namiesto písmena objaví žolík. Žolík platí za ktorékoľvek písmeno, ktoré tvorí platné slovo. Už dokončené slovo sa nepočíta.',
  htHideTitle: 'Skrývajúce sa písmená',
  htHideBody:
    'Na ťažších úrovniach sa odkryté písmeno môže samo zakryť a vrátiť sa neskôr v kole. Časomiera za to dostane takt navyše, načerveno. Už vybrané písmená sa nezakryjú.',
  tutHideBody:
    'Na ťažších úrovniach sa odkryté písmeno môže samo zakryť a vrátiť sa neskôr v kole. Už vybrané písmená sa nezakryjú.',
  htSwapTitle: 'Meniace sa písmená',
  htSwapBody:
    'Občas sa medzi kolami jedno písmeno vymení za iné. Uvidíte, ktoré zmizlo a ktoré pribudlo.',
  htLevelsTitle: 'Obťažnosti',
  htLevelEasy:
    'Rovnakých dvanásť písmen po celú hru, takže sa dajú naučiť a nosiť zoznam slov v hlave. Kamene sa odkrývajú pomaly a celá plocha zostáva vidieť dosť dlho na dokončenie výberu.',
  htLevelMedium:
    'Tu a tam sa písmeno zmení, takže je ťažšie pamätať si slová odložené na neskôr. Menej času na pozeranie aj na premýšľanie.',
  htLevelHard:
    'Trojpísmenové slová prestanú platiť a písmeno sa mení približne každé druhé kolo. Plocha sa sotva ukáže a už sa mieša.',
  htLevelInsane: 'Všetko naraz a naplno. Plocha sa zamieša takmer hneď po poslednom ťahu.',
  htTouchTitle: 'Dotyková obrazovka',
  htTouchBody:
    'Ťuknutím na odkrytý kameň vezmete jeho písmeno. Ťuknutím na vzaté písmeno ho vrátite. Dokončiť a Vymazať sú pod plochou.',

  accountTitle: 'Tvoj účet',
  menuProfile: 'Môj profil',
  menuGames: 'Moje hry',
  menuPublicPage: 'Moja verejná stránka',
  menuSignedInAs: 'Prihlásený ako',
  signIn: 'Prihlásiť sa',
  signOut: 'Odhlásiť sa',
  signInTitle: 'Prihlás sa alebo si založ účet',
  signInLead: 'Účet uchová tvoje hry, na každom zariadení.',
  signInKeepGame: 'Prihlás sa a táto hra ti zostane, aj všetky ďalšie.',
  continueWithApple: 'Pokračovať s Apple',
  continueWithGoogle: 'Pokračovať s Google',
  signInOr: 'alebo',
  emailLabel: 'E-mail',
  codeLabel: 'Kód',
  emailMeACode: 'Pošlite mi kód',
  working: 'Moment…',
  sendNewCode: 'Poslať nový kód',
  codeSent: 'Pozri sa do pošty. Kód platí 10 minút.',
  notNow: 'Teraz nie',
  badEmail: 'Toto nevyzerá ako e-mailová adresa.',
  badCode: 'Ten kód nefungoval. Kódy vypršia po 10 minútach a platia raz.',
  serverBusy: 'Server je nedostupný. Skús to o chvíľu.',
  serverDown: 'Server je nedostupný.',
  offline: 'Offline',
  leaderboardTitle: 'Tabuľka výsledkov',
  leaderboardEmpty: 'V tejto tabuľke ešte nie sú žiadne hry. Buď prvý.',
  leaderboardWouldBe: 'Kde by táto hra stála',
  leaderboardThisGame: 'Táto hra',
  leaderboardRanksHere: 'Kde táto hra stojí',
  leaderboardOutside: 'Mimo prvej päťky.',
  tutAccountTitle: 'Ulož si svoje skóre',
  tutAccountBody: 'Konto ukladá tvoje hry, zapisuje skóre do tabuľky a spravuje tvoj profil.',
  ssoCancelled: 'Prihlásenie zrušené.',
  ssoExpired: 'Trvalo to príliš dlho. Prihlás sa znova.',
  ssoNoUsername: 'Účet sa nepodarilo dokončiť. Skús to o chvíľu.',
  ssoFailed: 'Nepodarilo sa. Skús to znova, alebo použi e-mail.',
  tabProfile: 'Profil',
  countryAny: 'Radšej nepoviem',
  findCountry: 'Hľadať krajiny',
  usernameLabel: 'Používateľské meno',
  bioLabel: 'O mne',
  countryLabel: 'Krajina',
  bioHint: 'Žiadne odkazy. {left}/{max}',
  save: 'Uložiť',
  saving: 'Ukladám…',
  saved: 'Uložené.',
  nameTaken: 'To meno už niekto má.',
  nameTooShort: 'Trochu dlhšie, prosím.',
  nameTooLong: 'To je príliš dlhé.',
  nameBadCharacters: 'Iba písmená, číslice, spojovníky a podčiarkovníky.',
  nameBadEdges: 'Musí začínať a končiť písmenom alebo číslicou.',
  nameMixedScripts: 'Vyber jednu abecedu a zostaň pri nej.',
  nameReserved: 'Toto vyzerá ako meno, ktoré rozdávame my. Vyber iné.',
  nameUnusable: 'Toto meno sa nedá použiť.',
  bioTooLong: 'Popis má najviac {max} znakov.',
  bioHasLink: 'Odkazy v popise nie sú povolené.',
  bioHasControl: 'Obsahuje znaky, ktoré popis nemôže mať.',
  bioUnusable: 'Tento popis sa nedá použiť.',
  gamesHeading: 'Hry',
  gamesEmpty: 'Zatiaľ tu nič nie je. Hry, ktoré odohráš prihlásený, sa objavia tu.',
  gamesEmptyShort: 'Zatiaľ tu nič nie je.',
  gamesLoading: 'Načítavam tvoje hry…',
  theirGamesLoading: 'Načítavam ich hry…',
  profileLoading: 'Načítavam profil…',
  playerNotFound: 'Nikto sa tu tak nevolá.',
  columnWhen: 'Kedy',
  columnRounds: 'kôl',
  columnGame: 'Hra',
  gameLoading: 'Načítavam hru…',
  gameUnreadable: 'Túto hru sa nepodarilo načítať.',
  gameNotRanked: 'Hraná s upravenými pravidlami, takže sa nezapočítava.',
  detailNotKept: 'Slová a hracie plány tejto hry sa už neuchovávajú.',
  openingBoard: 'Úvodný plán',
  roundByRound: 'Kolo po kole',
  roundNothing: 'nič sa nenašlo',
  changeReplaced: '{letter} vymenené na mieste {slot}',
  changeWildOn: '{letter} žolík na mieste {slot}',
  changeWildOff: '{letter} žolík preč z miesta {slot}',
  keepThisGame: 'Zachovať túto hru',
  savedToYourGames: 'Uložené medzi tvoje hry.',

  reportThis: 'Nahlásiť',
  reportHeading: 'Nahlásiť toto',
  reportTheName: 'Meno',
  reportTheBio: 'Popis',
  reportTheScore: 'Skóre',
  reportWhy: 'Čo s tým nie je v poriadku? Nepovinné.',
  reportSend: 'Odoslať hlásenie',
  reportSent: 'Ďakujeme. Niekto sa na to pozrie.',
  reportSignIn: 'Prihlás sa, aby si mohol niečo nahlásiť.',
  reportGone: 'To už tu nie je.',
  reportCancel: 'Zrušiť',

  deleteAccount: 'Zmazať môj účet',
  deleteAccountWhat: 'Týmto sa navždy odstráni tvoj profil a každá uložená hra. Nedá sa to vrátiť.',
  deleteAccountAsk: 'Pošlite mi kód',
  deleteConfirm: 'Zmazať navždy',
  deleteGone: 'Tvoj účet je zmazaný. Ďakujeme, že si hral.',
  deleteNoAddress:
    'Na tomto účte nie je potvrdená adresa, takže zmazanie sa nedá overiť. Napíš nám a urobíme to ručne.',

  rulesetFacts: 'Slová od {letters} · nový kameň raz za {seconds}',
  plurals: {
    words: { one: '{n} slovo', few: '{n} slová', many: '{n} slova', other: '{n} slov' },
    rounds: { one: '{n} kolo', few: '{n} kolá', many: '{n} kola', other: '{n} kôl' },
    flips: { one: '{n} ťah', few: '{n} ťahy', many: '{n} ťahu', other: '{n} ťahov' },
    ticks: { one: '{n} takt', few: '{n} takty', many: '{n} taktu', other: '{n} taktov' },
    points: { one: '{n} bod', few: '{n} body', many: '{n} bodu', other: '{n} bodov' },
    minLetters: { one: '{n} písmena', few: '{n} písmen', many: '{n} písmena', other: '{n} písmen' },
    seconds: { one: '{n} sekundy', few: '{n} sekundy', many: '{n} sekundy', other: '{n} sekundy' },
  },
}
